/**
 * PanelSection - Collapsible section component for detail panels
 *
 * A reusable section container with:
 * - Optional header with title and icon
 * - Collapsible content
 * - Consistent styling
 */

import React, { useState } from 'react';

export interface PanelSectionProps {
  /** Section title */
  title?: string;
  /** Optional icon to show before title */
  icon?: React.ReactNode;
  /** Section content */
  children: React.ReactNode;
  /** Whether the section is collapsible */
  collapsible?: boolean;
  /** Initial collapsed state (only if collapsible) */
  defaultCollapsed?: boolean;
  /** Custom class name */
  className?: string;
  /** Actions to show in header */
  actions?: React.ReactNode;
  /** Whether to show a border */
  bordered?: boolean;
  /** Background style */
  variant?: 'default' | 'muted' | 'card';
}

export function PanelSection({
  title,
  icon,
  children,
  collapsible = false,
  defaultCollapsed = false,
  className = '',
  actions,
  bordered = true,
  variant = 'default',
}: PanelSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const variantClasses = {
    default: '',
    muted: 'bg-surface-muted',
    card: 'bg-surface rounded-xl shadow-sm border border-line',
  };

  const toggleCollapsed = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div
      className={`
        ${bordered && variant === 'default' ? 'border-b border-line' : ''}
        ${variantClasses[variant]}
        ${className}
      `}
    >
      {title && (
        <div
          className={`
            flex items-center justify-between
            ${variant === 'card' ? 'px-4 py-3' : 'py-3'}
            ${collapsible ? 'cursor-pointer select-none' : ''}
          `}
          onClick={toggleCollapsed}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? !isCollapsed : undefined}
        >
          <div className="flex items-center gap-2">
            {collapsible && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`
                  text-icon-muted transition-transform duration-200
                  ${isCollapsed ? '-rotate-90' : ''}
                `}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            )}
            {icon && (
              <span className="text-icon-muted">{icon}</span>
            )}
            <h3 className="text-sm font-semibold text-text">
              {title}
            </h3>
          </div>
          {actions && !isCollapsed && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {actions}
            </div>
          )}
        </div>
      )}
      {(!collapsible || !isCollapsed) && (
        <div
          className={`
            ${variant === 'card' ? 'px-4 pb-4' : 'pb-4'}
            ${title ? '' : variant === 'card' ? 'p-4' : 'py-4'}
          `}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default PanelSection;
